import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import DoxWorkPerformance from "@/components/DoxWorkPerformance";

const DoxWorkContext = createContext(null);

/**
 * Provider that gives any component access to `runDoxWork()` — the one-shot
 * Assign → Depart → Work → Return performance around an async backend call.
 *
 * Usage:
 *   const { runDoxWork } = useDoxWork();
 *   runDoxWork({
 *     ackLine: "I've got it.",
 *     deliverLine: "Everything checked out.",
 *     stages: [...],
 *     run: () => api.post("/documents/123/approve"),
 *     onResolved: (res) => toast.success("..."),
 *   });
 */
export function DoxWorkProvider({ children }) {
  const [open, setOpen] = useState(false);
  const configRef = useRef(null);

  const runDoxWork = useCallback((config) => {
    configRef.current = config || {};
    setOpen(true);
  }, []);

  // Test hook — lets automation trigger the performance without hunting for
  // an approve button in production data. Safe to leave in; it only wires a
  // window function that maps to the same public API.
  useEffect(() => {
    window.__runDoxWork = (cfg) => runDoxWork(cfg || {
      ackLine: "I've got it.",
      deliverLine: "Done.",
      run: () => new Promise((r) => setTimeout(() => r({ data: { ok: true } }), 400)),
    });
    return () => { try { delete window.__runDoxWork; } catch { /* noop */ } };
  }, [runDoxWork]);

  const close = useCallback(() => setOpen(false), []);

  return (
    <DoxWorkContext.Provider value={{ runDoxWork }}>
      {children}
      <DoxWorkPerformance open={open} config={configRef.current} onClose={close} />
    </DoxWorkContext.Provider>
  );
}

export function useDoxWork() {
  const ctx = useContext(DoxWorkContext);
  if (!ctx) return { runDoxWork: null, available: false };
  return { ...ctx, available: true };
}
